// An allowed value may hold fill-in blanks, runs of two or more underscores:
// "проводилась __ месяца назад". As on the server, a blank is filled together
// with the word of letters after exactly one space, so the unit may change
// ("проводилась 1 год назад"); punctuation after the unit stays template text.
// The text around these regions is compared ignoring case and spacing.
const BLANK_RUN = /_{2,}/u;
const BLANK_REGIONS = /_{2,}(?: \p{L}+)?/gu;

const words = (text: string) => text.split(/\s+/u).filter(Boolean);

const normalizeValue = (text: string) => words(text).join(' ').toLowerCase();

const escapePattern = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');

export const hasBlanks = (allowed: string) => BLANK_RUN.test(allowed);

// The template text around the fill regions: one part more than there are
// regions.
export const splitBlanks = (allowed: string) => allowed.split(BLANK_REGIONS);

// The unit after each blank, empty for a bare blank.
export const getBlankUnits = (allowed: string) =>
  [...allowed.matchAll(BLANK_REGIONS)].map(([region]) =>
    region.replace(BLANK_RUN, '').trim(),
  );

// The text of every region when `value` fills all of them, otherwise null: the
// unfilled literal and a half-filled value are not fills.
export const matchBlankFills = (
  allowed: string,
  value: string,
): string[] | null => {
  const parts = splitBlanks(allowed);
  if (parts.length < 2) {
    return null;
  }
  const pattern = new RegExp(
    `^${parts
      .map((part) => words(part).map(escapePattern).join('\\s+'))
      .join('\\s*(.+?)\\s*')}$`,
    'iu',
  );
  const fills = words(value).join(' ').match(pattern)?.slice(1);
  return fills?.every((fill) => fill.trim() && !BLANK_RUN.test(fill))
    ? fills.map((fill) => fill.trim())
    : null;
};

// The stored form: the template's text with the fills in place of the regions.
// Without every fill it is the literal, which the server keeps as chosen.
export const fillBlanks = (allowed: string, fills: string[]) => {
  const trimmed = fills.map((fill) => fill.trim());
  const parts = splitBlanks(allowed);
  if (trimmed.length < parts.length - 1 || trimmed.some((fill) => !fill)) {
    return allowed;
  }
  return parts.reduce(
    (text, part, index) => `${text}${trimmed[index - 1]}${part}`,
  );
};

// The allowed value a stored value stands for. An exact match wins over a
// blank that the value would fill.
export const findAllowedValue = (allowed: string[], value: string) => {
  const normalized = normalizeValue(value);
  if (!normalized) {
    return undefined;
  }
  return (
    allowed.find((item) => normalizeValue(item) === normalized) ??
    allowed.find((item) => matchBlankFills(item, value) !== null)
  );
};

export const isAllowedValue = (allowed: string[], value: string) =>
  findAllowedValue(allowed, value) !== undefined;
